import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ActionButton,
  DevSection,
  JsonViewer,
  ResultPanel,
} from "./-_dev-components";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ResultState = {
  data: unknown;
  error: unknown;
  timestamp: number;
  label: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ChecklistStatusBadge({ status }: { status: string }) {
  const color =
    status === "MISSING"
      ? "bg-red-500/20 text-red-400 border-red-500/30"
      : status === "UPLOADED"
        ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
        : status === "APPROVED"
          ? "bg-green-500/20 text-green-400 border-green-500/30"
          : "bg-muted text-muted-foreground border-border";

  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ChecklistPlaygroundPage() {
  const audits = useQuery(api.audit_queries.listAudits, {});

  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  const selectedAudit = audits?.find((a) => a.id === selectedAuditId) ?? null;

  const items = useQuery(
    api.checklist_queries.getItemsByAuditId,
    selectedAudit ? { auditId: selectedAudit.id } : "skip",
  );

  const categories = useQuery(
    api.checklist_queries.getCategoriesByAuditId,
    selectedAudit ? { auditId: selectedAudit.id } : "skip",
  );

  const selectedItem = items?.find((i) => i._id === selectedItemId) ?? null;

  const categoryMap = new Map((categories ?? []).map((c) => [c._id, c.name]));

  // Group items by category
  const grouped = new Map<Id<"checklistCategories">, typeof items>();
  if (items) {
    for (const item of items) {
      const catId = item.categoryId;
      const existing = grouped.get(catId) ?? [];
      grouped.set(catId, [...existing, item]);
    }
  }

  function handleAuditChange(id: string) {
    setSelectedAuditId(id);
    setSelectedItemId(null);
    setResult(null);
  }

  function handleItemSelect(id: string) {
    setSelectedItemId(id);
    setResult(null);
  }

  function makeResultHandler(label: string) {
    return (r: { data: unknown; error: unknown; timestamp: number }) =>
      setResult({ ...r, label });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden font-mono text-sm">
      {/* Audit selector bar */}
      <div className="flex items-center gap-3 border-b border-border bg-muted/20 px-4 py-2">
        <label className="text-xs font-medium text-muted-foreground">
          Audit:
        </label>
        <select
          value={selectedAuditId ?? ""}
          onChange={(e) => handleAuditChange(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">— select an audit —</option>
          {(audits ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.displayName}
            </option>
          ))}
        </select>
        {selectedAudit && (
          <span className="text-xs text-muted-foreground">
            Selected:{" "}
            <span className="text-foreground">
              "{selectedAudit.displayName}"
            </span>{" "}
            (id: …{selectedAudit.id.slice(-8)}) | Status:{" "}
            <span className="text-foreground">{selectedAudit.status}</span>
          </span>
        )}
      </div>

      {/* Main content: list + actions + live events */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — checklist items */}
        <div className="flex w-80 shrink-0 flex-col border-r border-border p-3">
          {!selectedAuditId ? (
            <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
              Select an audit above
            </div>
          ) : (
            <>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Checklist Items ({items?.length ?? "…"})
              </h2>
              {items === undefined ? (
                <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
                  No items
                </div>
              ) : (
                <div
                  className="overflow-auto rounded border border-border"
                  style={{ maxHeight: "calc(100vh - 200px)" }}
                >
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-muted">
                      <tr>
                        <th className="border-b border-border px-3 py-2 text-left font-semibold text-muted-foreground">
                          ID
                        </th>
                        <th className="border-b border-border px-3 py-2 text-left font-semibold text-muted-foreground">
                          Description
                        </th>
                        <th className="border-b border-border px-3 py-2 text-left font-semibold text-muted-foreground">
                          Status
                        </th>
                        <th className="border-b border-border px-3 py-2 text-left font-semibold text-muted-foreground">
                          Sum
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(grouped.entries()).map(
                        ([catId, catItems]) => (
                          <>
                            {/* Category header row */}
                            <tr key={`cat-${catId}`}>
                              <td
                                colSpan={4}
                                className="bg-muted/50 px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                              >
                                {categoryMap.get(catId) ??
                                  `Category …${catId.slice(-8)}`}
                              </td>
                            </tr>
                            {/* Item rows */}
                            {(catItems ?? []).map((item) => {
                              const selected = item._id === selectedItemId;
                              return (
                                <tr
                                  key={item._id}
                                  onClick={() => handleItemSelect(item._id)}
                                  className={`cursor-pointer border-b border-border last:border-0 hover:bg-muted/50 ${
                                    selected
                                      ? "bg-primary/10 hover:bg-primary/20"
                                      : ""
                                  }`}
                                >
                                  <td className="px-3 py-2 font-mono text-muted-foreground">
                                    …{item._id.slice(-8)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span title={item.description}>
                                      {item.description.length > 28
                                        ? `${item.description.slice(0, 28)}…`
                                        : item.description}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <ChecklistStatusBadge
                                      status={item.status}
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-muted-foreground">
                                    {item.summaryReport ? "✓" : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {!selectedItem ? (
          <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
            Select a checklist item to inspect and run actions
          </div>
        ) : (
          <>
            {/* Middle panel — actions */}
            <div className="flex w-[480px] shrink-0 flex-col overflow-y-auto border-r border-border p-4 space-y-4">
              {/* Header */}
              <div className="space-y-0.5">
                <h2 className="font-semibold truncate">
                  {selectedItem.description.length > 55
                    ? `${selectedItem.description.slice(0, 55)}…`
                    : selectedItem.description}
                </h2>
                <span className="font-mono text-xs text-muted-foreground">
                  …{selectedItem._id.slice(-8)}
                </span>
              </div>

              {/* Raw data viewer */}
              <JsonViewer data={selectedItem} label="Raw Checklist Item" />

              {/* AI Summary */}
              <AISummarySection
                selectedItem={selectedItem}
                selectedAudit={selectedAudit}
                summaryReport={selectedItem.summaryReport}
                onResult={makeResultHandler}
              />

              {/* AI Content Generation */}
              <AIContentSection
                selectedItem={selectedItem}
                selectedAudit={selectedAudit}
                onResult={makeResultHandler}
              />

              {/* Document Matching */}
              <DocumentMatchingSection
                selectedItem={selectedItem}
                suggestedDocuments={selectedItem.suggestedDocuments}
                onResult={makeResultHandler}
              />

              {/* Completion */}
              <CompletionSection
                selectedItem={selectedItem}
                currentStatus={selectedItem.status}
                onResult={makeResultHandler}
              />

              {/* Persistent result panel */}
              <div className="border-t border-border pt-4">
                <ResultPanel result={result} />
              </div>
            </div>

            {/* Right panel — live events feed */}
            <div className="flex flex-1 flex-col overflow-hidden">
              <ChecklistLiveEventsPanel selectedItem={selectedItem} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Checklist Live Events Panel
// ---------------------------------------------------------------------------

function ChecklistLiveEventsPanel({
  selectedItem,
}: {
  selectedItem: any; // TODO: Add proper type for checklist item
}) {
  const activities = useQuery(
    api.checklist_queries.getChecklistItemActivities,
    selectedItem ? { checklistItemId: selectedItem._id, limit: 30 } : "skip",
  );

  // Derive isProcessing from most recent events
  const isProcessing =
    activities != null &&
    activities.length > 0 &&
    activities[0].type !== "failure" &&
    (activities[0].type === "summary" || activities[0].type === "search") &&
    activities[0].description.toLowerCase().includes("started");

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Live Events
        </span>
        {isProcessing && (
          <span className="flex items-center gap-1.5 text-xs text-yellow-600">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-500" />
            Processing…
          </span>
        )}
        {!isProcessing && activities !== undefined && (
          <span className="text-xs text-muted-foreground">
            {activities.length} events
          </span>
        )}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {activities === undefined && (
          <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
            Loading…
          </div>
        )}
        {activities?.length === 0 && (
          <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
            No events yet
          </div>
        )}
        {activities?.map((event) => (
          <ChecklistEventRow key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Checklist event row
// ---------------------------------------------------------------------------

type ChecklistActivityType =
  | "summary"
  | "search"
  | "completion"
  | "failure"
  | "update"
  | "question";

interface ChecklistActivity {
  id: string;
  type: ChecklistActivityType;
  description: string;
  timestamp: number;
  userName: string | null;
  metadata: unknown;
}

const CHECKLIST_TYPE_STYLES: Record<
  ChecklistActivityType,
  { pill: string; bar: string }
> = {
  failure: {
    pill: "bg-red-100 text-red-700 border-red-300",
    bar: "bg-red-400",
  },
  summary: {
    pill: "bg-green-100 text-green-700 border-green-300",
    bar: "bg-green-400",
  },
  completion: {
    pill: "bg-purple-100 text-purple-700 border-purple-300",
    bar: "bg-purple-400",
  },
  search: {
    pill: "bg-blue-100 text-blue-700 border-blue-300",
    bar: "bg-blue-400",
  },
  update: {
    pill: "bg-zinc-100 text-zinc-600 border-zinc-300",
    bar: "bg-zinc-400",
  },
  question: {
    pill: "bg-amber-100 text-amber-700 border-amber-300",
    bar: "bg-amber-400",
  },
};

function ChecklistEventRow({ event }: { event: ChecklistActivity }) {
  const [expanded, setExpanded] = useState(false);
  const style =
    CHECKLIST_TYPE_STYLES[event.type] ?? CHECKLIST_TYPE_STYLES.update;
  const ago = formatAgo(event.timestamp);

  return (
    <div className="rounded border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-2 px-2 py-1.5 text-left hover:bg-muted/30 transition-colors"
      >
        <span className={`mt-0.5 h-3 w-1 shrink-0 rounded-full ${style.bar}`} />
        <span
          className={`shrink-0 rounded border px-1 py-px text-[10px] font-semibold uppercase ${style.pill}`}
        >
          {event.type}
        </span>
        <span className="flex-1 text-xs leading-tight">
          {event.description}
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
          {ago}
        </span>
        {event.metadata != null && (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </button>

      {expanded && event.metadata != null && (
        <div className="border-t border-border bg-muted/10 px-2 py-1.5">
          <pre className="text-[10px] leading-relaxed overflow-auto max-h-40">
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// AI Summary section
// ---------------------------------------------------------------------------

function AISummarySection({
  selectedItem,
  selectedAudit,
  summaryReport,
  onResult,
}: {
  selectedItem: any; // TODO: Add proper type for checklist item
  selectedAudit: any; // TODO: Add proper type for audit
  summaryReport: string | undefined;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const generateSummary = useMutation(
    api.checklist_mutations.generateChecklistItemSummary,
  );

  return (
    <DevSection title="AI Summary">
      <ActionButton
        label="Generate Summary"
        onResult={onResult("Generate Summary")}
        onClick={async () =>
          generateSummary({
            checklistItemId: selectedItem?._id!,
            auditId: selectedAudit?.id!,
          })
        }
      />
      <JsonViewer data={summaryReport ?? null} label="Current summaryReport" />
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// AI Content Generation section
// ---------------------------------------------------------------------------

function AIContentSection({
  selectedItem,
  selectedAudit,
  onResult,
}: {
  selectedItem: any; // TODO: Add proper type for checklist item
  selectedAudit: any; // TODO: Add proper type for audit
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const generateInformation = useMutation(
    api.checklist_mutations.generateChecklistItemInformation,
  );
  const generateExamples = useMutation(
    api.checklist_mutations.generateChecklistItemExamples,
  );

  return (
    <DevSection title="AI Content Generation">
      <div className="flex flex-wrap gap-2">
        <ActionButton
          label="Generate Information"
          onResult={onResult("Generate Information")}
          onClick={async () =>
            generateInformation({
              checklistItemId: selectedItem?._id!,
              auditId: selectedAudit?.id!,
            })
          }
        />
        <ActionButton
          label="Generate Examples"
          onResult={onResult("Generate Examples")}
          onClick={async () =>
            generateExamples({
              checklistItemId: selectedItem?._id!,
              auditId: selectedAudit?.id!,
            })
          }
        />
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Document Matching section
// ---------------------------------------------------------------------------

function DocumentMatchingSection({
  selectedItem,
  suggestedDocuments,
  onResult,
}: {
  selectedItem: any; // TODO: Add proper type for checklist item
  suggestedDocuments: unknown;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const findRelatedDocuments = useMutation(
    api.checklist_mutations.findRelatedDocumentsForChecklistItem,
  );

  return (
    <DevSection title="Document Matching">
      <ActionButton
        label="Find Related Documents"
        onResult={onResult("Find Related Documents")}
        onClick={async () =>
          findRelatedDocuments({
            checklistItemId: selectedItem._id,
          })
        }
      />
      <JsonViewer
        data={suggestedDocuments ?? null}
        label="Current suggestedDocuments"
      />
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Completion section
// ---------------------------------------------------------------------------

function CompletionSection({
  selectedItem,
  currentStatus,
  onResult,
}: {
  selectedItem: any; // TODO: Add proper type for checklist item
  currentStatus: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const completeItem = useMutation(
    api.checklist_mutations.completeChecklistItem,
  );
  const uncompleteItem = useMutation(
    api.checklist_mutations.uncompleteChecklistItem,
  );

  return (
    <DevSection title="Completion">
      {/* Status legend */}
      <div className="space-y-1 text-xs text-muted-foreground">
        <p className="font-medium">Current status:</p>
        <div className="flex items-center gap-2">
          <ChecklistStatusBadge status={currentStatus} />
        </div>
        <div className="mt-2 space-y-0.5 text-muted-foreground">
          <p>
            <span className="font-mono text-red-400">MISSING</span> — no
            document linked
          </p>
          <p>
            <span className="font-mono text-amber-400">UPLOADED</span> —
            document linked, awaiting approval
          </p>
          <p>
            <span className="font-mono text-green-400">APPROVED</span> — item
            completed
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <ActionButton
          label="Complete Item"
          onResult={onResult("Complete Item")}
          onClick={async () =>
            completeItem({
              checklistItemId: selectedItem?._id!,
            })
          }
        />
        <ActionButton
          label="Uncomplete Item"
          onResult={onResult("Uncomplete Item")}
          onClick={async () =>
            uncompleteItem({
              checklistItemId: selectedItem?._id!,
            })
          }
        />
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/dev/checklist")({
  component: ChecklistPlaygroundPage,
});
